import { useState, useContext, useEffect, useRef } from 'react'
import moment from 'moment-timezone'
import generatePDF from 'react-to-pdf';
import { useNavigate } from 'react-router-dom'
import { Card, Grid, Box, Divider, Paper, SvgIcon, Stack, Typography, Button } from '@mui/material'
import CustomToolTip from '../common/toolTip'
import {
  savingsIcon,
  sunshineReportSolarIcon,
  leafIcon,
  carIcon,
  hollowCircleInfoIcon,
  solarIconPath,
  downloadIconPath, 
} from '../common/iconPaths'
import { DataContext, DataDispatchContext } from '../../contexts/data/dataContext'
import { analyticsPage, analyticsDomain, analyticsCta } from '../../services/constants'
import { sendClickAnalytics, sendViewAnalytics } from '../common/util/util'
import custom from '../../styles/mui/szVariables.json'
import SolarGenerationGauge from './SolarGenerationGauge'
import EnergyConsumptionChart from './EnergyConsumptionChart'
import Referral from '../right-panel/referral/referral'

export default function SavingsReport({ id }: any) {
  const cardIcons = [savingsIcon, sunshineReportSolarIcon, leafIcon]
  const targetRef = useRef();
  const navigate = useNavigate()
  const data = useContext(DataContext)
  const dispatch: any = useContext(DataDispatchContext)
  const [ pdf, setPdf ] = useState(false)
  const selectedData = (data?.savings || []).find((r:any)=>r.id===data?.savingsSelected)
  const border = custom.border

  useEffect(() => {
    sendViewAnalytics('/savings', {
      domain: analyticsDomain.savings,
      name: analyticsPage.sunshineReport,
    })
  }, [])

  const handleReferral = () => {
    window.scrollTo(0,0)
    dispatch({
        type: 'setRightPanel',
        rightPanel: <Referral key="referral"/>
    })
    sendClickAnalytics('/savings', {
          domain: analyticsDomain.savings,
          name: analyticsCta.tileReferFriend,
          metadata: {},
        })
  }

  function handleContactUs() {
    dispatch({
      type: 'setAcctSelected',
      acctSelected: 'help'
    })
    sendClickAnalytics('/savings', {
      domain: analyticsDomain.savings,
      name: analyticsCta.contactUs,
      metadata: {},
    })
    navigate('/profile')
  }


  const formatDateRange = (startDate: string, endDate: string) => {
    const formattedStart = moment(startDate).format('MMMM yyyy')
    const formattedEnd = moment(endDate).format('MMMM yyyy')
    return `${formattedStart} - ${formattedEnd}`
  }

  function alwaysTwoDecimal(number: number) {
    if(!number) return '0.00'
    return Intl.NumberFormat('en-EN',{ 
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    }).format(number);
  }

  const downloadPdf = async () => {
    setPdf(true)
  };

  useEffect(()=>{
    if(pdf) {
      generatePDF(targetRef, {
        filename: `${selectedData?.id}_${data?.customer?.account?.siteId}.pdf`})
      .then(()=>setPdf(false))
    }
  },[pdf])

  return (
    <Box
      ref={targetRef}
      id={'AnnualSavings'}
      px={custom.spacing.md}
      pt={{
        xs: custom.spacing.xxl,
        sm: data?.rightPanel ? custom.spacing.md : 0,
        md: data?.rightPanel ? custom.spacing.md : 0,
        lg: 0,
    }}>
    <Paper
      elevation={0}
      sx={{
        borderRadius: custom.corners4,
        border,
        width: '100%',
        boxSizing: 'border-box',
      }}>
        <Box px={custom.spacing.md} pt={custom.spacing.xl} >
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="column" mr={custom.spacing.md}>
              <Typography variant="szHeading.Bold.Large" gutterBottom
                id="reportTitle">
                {selectedData?.title}
              </Typography>
              <Typography variant="szBody.Regular.Medium"
               id="subTitle">{selectedData?.subTitle}</Typography>
            </Stack>
            {!pdf && 
              <Stack direction="row">
                <CustomToolTip message="Download as pdf">
                  <Box id='downloadPDF'
                    onClick={downloadPdf}
                    sx={{
                      padding: custom.spacing.xxxs,
                      width: '24px',
                      height: '24px',
                      border: custom.border,
                      borderRadius: custom.corners3,
                      cursor: 'pointer'
                    }}>
                    <SvgIcon viewBox='0 0 20 20'>
                        {downloadIconPath}
                    </SvgIcon>
                  </Box>
                </CustomToolTip>
              </Stack>
            }
          </Stack>

          <Divider sx={{ 
              marginTop: custom.spacing.md, 
              marginBottom: custom.spacing.lg 
              }} />

          {/* Your 12 months savings at a glance */}
          <Box sx={{ marginBottom: custom.spacing.lg }} 
            display="flex" flexDirection={"column"}
            >
            <Stack direction={{
                xs: "column",
                sm: data?.rightPanel?"column":"row",
                md: data?.rightPanel?"column":"row",
                lg: "row"
              }} 
              justifyContent="space-between">
              <Typography variant="szHeading.Bold.Small" 
                id="savingsSummaryTitle" gutterBottom>
                {selectedData?.savingsSummary?.title}
              </Typography>
              <Typography variant="szBody.Regular.Medium" 
                id="reportDates" gutterBottom>
                {formatDateRange(selectedData?.startDate, selectedData?.endDate)}
              </Typography>
            </Stack>
            <Grid container spacing={custom.spacing.sm} 
               justifyContent={"center"}>
              {selectedData?.savingsSummary?.sections.map((section: any, index: any) => (
                <Grid item xs={12} 
                  sm={data?.rightPanel?12:index===2?12:6} 
                  md={index===2?12:6} 
                  lg={data?.rightPanel?index===2?12:6:4} 
                  key={section.id} width="100%">
                  <Card id={`${section.id}-card`}
                        elevation={0}
                        sx={{ 
                          border: custom.border, 
                          borderRadius: custom.corners5, 
                          backgroundColor: 'sz.colors.sunshine', 
                          p: custom.spacing.md,
                        }}>
                    <Stack justifyContent='flex-start' 
                           direction={"column"}
                           maxHeight={"200px"}
                           display={"flex"}
                           alignContent={"flex-end"}>
                        <Stack direction="row" 
                               justifyContent="space-between"
                               pb={custom.spacing.md}>
                            <SvgIcon sx={{ fontSize: 64 }}>
                                {cardIcons[index % cardIcons.length]}
                            </SvgIcon>
                            <CustomToolTip message={section.tooltip}>
                              <SvgIcon sx={{ fontSize: 20 }}>
                                  {hollowCircleInfoIcon}
                              </SvgIcon>
                            </CustomToolTip>
                        </Stack>
                        <Box>
                          <Typography variant="szBody.Regular.Medium"
                            id={`${section.id}CardDescription`}>
                            {section.title}
                          </Typography>
                          <Typography 
                              id={`${section.id}CardValue`}
                              component={"p"}
                              mt={custom.spacing.xxs}
                              variant={data?.rightPanel?"szHeading.Bold.Small":"szHeading.Bold.Large"}>
                              {index===0?section.value:`${section.value.replace(',','')}`}
                          </Typography>
                        </Box>
                    </Stack>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Your energy savings over the last 12 months */}
          <Box sx={{ marginBottom: custom.spacing.lg }} >
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="szHeading.Bold.Small" 
                id="savingsCostTitle" gutterBottom>
                {selectedData?.savingsCost?.title}
              </Typography>
              <CustomToolTip message={selectedData?.savingsCost?.tooltip}>
              <SvgIcon sx={{ fontSize: 20 }}>
                  {hollowCircleInfoIcon}
              </SvgIcon>
              </CustomToolTip>
            </Stack>
            <Paper
              id="savingsLast12Months"
              elevation={0}
              sx={{
                border: custom.border,
                borderRadius: custom.corners3,
                p: custom.spacing.md
              }}>
              <Stack direction={{
                        xs:"column-reverse",
                        sm:"column-reverse",
                        md:"column-reverse",
                        lg:data?.rightPanel?"column-reverse":"row"
                      }}
                      alignItems="center"
                      spacing={custom.spacing.sm}>
                <Box width={{
                        xs:"100%",
                        sm:"100%",
                        md:"100%",
                        lg:data?.rightPanel?"100%":"600px"
                      }}
                      mt={custom.spacing.sm} 
                      >
                  <Typography variant="szBody.Regular.Small"
                    id="savingsCostMessage">
                    {selectedData?.savingsCost?.message}
                  </Typography>
                </Box>
                <Box>
                <Grid container spacing={custom.spacing.xxs}>
                  {[
                    { id: 'previousEnergyCost', 
                      data: selectedData?.savingsCost?.previousEnergyCost, 
                      color: 'sz.colors.background' },
                    { id: 'currentEnergyCost', 
                      data: selectedData?.savingsCost?.currentEnergyCost, 
                      color: 'sz.colors.background' },
                    { id: 'totalEnergySavings', 
                      data: selectedData?.savingsCost?.totalEnergySavings, 
                      color: 'battery.main' },
                  ].map((item, index) => (
                    <Grid item xs={12} id={item.id}
                          sm={data?.rightPanel?12:4} md={4} lg={4} key={index}>
                      <Stack
                        direction="column"
                        justifyContent="space-between"
                        sx={{
                          minHeight: {
                            xs: '100px',
                            sm: data?.rightPanel?'100px':'176px',
                            md: '176px',
                            lg: '176px',
                          },
                          borderRadius: custom.corners4,
                          backgroundColor: item.color,
                          padding: custom.spacing.sm,
                        }}>
                        <Typography
                          variant="szBody.Regular.Small"
                          color={item.color === 'battery.main' ? '#FFFFFF' : 'inherit'}
                          id={`${item.id}Title`}>
                          {item.data?.title}
                        </Typography>
                        <Typography
                          variant={data?.rightPanel?"szHeading.Bold.Small":"szHeading.Bold.Medium"}
                          color={item.color === 'battery.main' ? '#FFFFFF' : 'inherit'}
                          id={`${item.id}Value`}>
                          ${alwaysTwoDecimal(item.data?.value)}
                        </Typography>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
                </Box>
              </Stack>
            </Paper>
          </Box>

          {/* Your personal carbon offset */}
          <Box sx={{ marginBottom: custom.spacing.lg }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="szHeading.Bold.Small" 
                 id="carbonSavingsTitle" gutterBottom>
                {selectedData?.carbonSavings?.title}
              </Typography>
              <CustomToolTip message={selectedData?.carbonSavings?.tooltip}>
              <SvgIcon sx={{ fontSize: 20 }}>{hollowCircleInfoIcon}</SvgIcon>
              </CustomToolTip>
            </Stack>
            <Paper
              id="carbonSavings"
              elevation={0}
              sx={{
                padding: custom.spacing.md,
                border: custom.border,
                borderRadius: custom.corners3,
              }}>
              <Stack direction={{
                        xs:"column-reverse",
                        sm:"column-reverse",
                        md:"column-reverse",
                        lg:data?.rightPanel?"column-reverse":"row"
                      }}  
                      alignItems="center">
                <Box width={{
                        xs:"100%",
                        sm:"100%",
                        md:"100%",
                        lg:data?.rightPanel?"100%":"600px"
                      }}  
                      >
                  <Stack direction="column" pt={custom.spacing.sm}
                    justifyContent="space-between">
                    <Typography variant="szHeading.Bold.xSmall" 
                      id="carbonSavingsMessageTitle" gutterBottom>
                      {selectedData?.carbonSavings?.messageTitle}
                    </Typography>
                    <Typography variant="szBody.Regular.Small"
                      id="carbonSavingsMessage">
                      {selectedData?.carbonSavings?.message}
                    </Typography>
                  </Stack>
                </Box>
                <Grid container spacing={custom.spacing.sm} 
                      display="flex" direction="row"
                      justifyContent="center" 
                      alignItems="flex-start">
                  <Grid item xs={12} sm={data?.rightPanel?12:6} md={6} lg={6}
                      height="100%">
                    <Stack 
                      direction="column"
                      justifyContent="flex-start"
                      sx={{
                        minHeight: data?.rightPanel?"130px":"160px",
                        borderRadius: custom.corners4,
                        backgroundColor: 'sz.colors.background',
                        padding: custom.spacing.sm,
                      }}>
                      <SvgIcon sx={{fontSize: 64 }}>
                          {leafIcon}
                      </SvgIcon>
                      <Typography variant="szBody.Regular.Small"
                         id="equivalentTrees" mt={custom.spacing.sm}>
                        {selectedData?.carbonSavings?.equivalentTrees}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={data?.rightPanel?12:6} md={6} lg={6}
                    height="100%">
                    <Stack
                      direction="column"
                      justifyContent="flex-start"
                      sx={{
                        minHeight: data?.rightPanel?"130px":"160px",
                        borderRadius: custom.corners4,
                        backgroundColor: 'sz.colors.background',
                        padding: custom.spacing.sm,
                      }}>
                      <SvgIcon sx={{ fontSize: 64 }}>
                        {carIcon}
                      </SvgIcon>
                      <Typography variant="szBody.Regular.Small"
                        id="equivalentCarKm" mt={custom.spacing.sm}>
                          {selectedData?.carbonSavings?.equivalentCarKm?.replace(',','')}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Paper>
          </Box>

          {/* The good energy you've generated */}
          <Box sx={{ marginBottom: custom.spacing.lg }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="szHeading.Bold.Small" 
                id="solarGenerationTitle" gutterBottom>
                {selectedData?.solarGenerationChart?.title}
              </Typography>
              <CustomToolTip message={selectedData?.solarGenerationChart?.tooltip}>
              <SvgIcon sx={{ fontSize: 20 }}>{hollowCircleInfoIcon}</SvgIcon>
              </CustomToolTip>
            </Stack>
            <Paper
              id="goodEnergyGenerated"
              elevation={0}
              sx={{
                minHeight: "180px",
                padding: custom.spacing.md,
                border: custom.border,
                borderRadius: custom.corners3,
              }}>
              <Stack direction={{
                  xs: 'column-reverse',
                  sm: 'column-reverse',
                  md: 'column-reverse',
                  lg: data?.rightPanel?'column-reverse':'row',
                }} 
                justifyContent={"space-between"}
                alignItems={"center"}>
                <Box width={{
                        xs: "100%",
                        sm: "100%",
                        md: "100%",
                        lg: data?.rightPanel?"100%":"550px"
                      }}  
                      mt={custom.spacing.sm}>
                  <Stack direction="column" justifyContent="space-between">
                    <Typography variant="szHeading.Bold.xSmall" 
                      id="solarGenerationMessageTitle" gutterBottom>
                      {selectedData?.solarGenerationChart?.messageTitle}
                    </Typography>
                    <Typography variant="szBody.Regular.Small"
                      id="solarGenerationMessage">
                      {selectedData?.solarGenerationChart?.message}
                      </Typography>
                  </Stack>
                </Box>
                <Box display="flex" justifyContent="center" alignItems="flex-end"
                   id="solarGenerationGauge" maxWidth="250px">
                   {selectedData?.solarGenerationChart?.chart &&   
                    <SolarGenerationGauge
                      id="solarGeneration"
                      value={selectedData?.solarGenerationChart?.chart?.actualSolarGeneration}
                      min={selectedData?.solarGenerationChart?.chart?.minValue}
                      max={selectedData?.solarGenerationChart?.chart?.maxValue}
                      expectedLow={selectedData?.solarGenerationChart?.chart?.expectedRangeLow}
                      expectedHigh={selectedData?.solarGenerationChart?.chart?.expectedRangeHigh}
                      label="Total solar generated"
                    />
                   }
                </Box>
              </Stack>
            </Paper>
          </Box>

          {/* How much energy you’ve been consuming */}
          <Box sx={{ marginBottom: custom.spacing.lg }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="szHeading.Bold.Small" 
                id="energyConsumptionTitle" gutterBottom>
                {selectedData?.energyConsumptionGauge?.title}
              </Typography>
              <CustomToolTip message={selectedData?.energyConsumptionGauge?.tooltip}>
              <SvgIcon sx={{ fontSize: 20 }}>{hollowCircleInfoIcon}</SvgIcon>
              </CustomToolTip>
            </Stack>
            <Paper
              id="energyConsumed"
              elevation={0}
              sx={{
                // height: 180,
                padding: custom.spacing.md,
                border: custom.border,
                borderRadius: custom.corners3,
              }}>
              <Stack direction={{
                  xs: "column-reverse",
                  sm: "column-reverse",
                  md: "column-reverse",
                  lg: data?.rightPanel?"column-reverse":"row",
                }} 
                alignItems={"center"}
                justifyContent="space-between">
                <Box width={{
                        xs:"100%",
                        sm:"100%",
                        md:"100%",
                        lg:data?.rightPanel?"100%":"550px"
                      }}  
                      mt={custom.spacing.sm}>
                  <Stack direction="column" justifyContent="space-between">
                    <Typography variant="szHeading.Bold.xSmall" 
                      id="energyConsumptionMessageTitle" gutterBottom>
                      {selectedData?.energyConsumptionGauge?.messageTitle}
                    </Typography>
                    <Typography variant="szBody.Regular.Small"
                      id="energyConsumptionMessage"> 
                      {selectedData?.energyConsumptionGauge?.message}
                    </Typography>
                  </Stack>
                </Box>
                <Box display="flex" justifyContent="center" 
                  id="energyConsumptionGauge" alignItems="center"
                  maxWidth={"250px"}>
                    {selectedData?.energyConsumptionGauge?.chart && 
                    <SolarGenerationGauge
                      id="energyConsumption"
                      value= {selectedData?.energyConsumptionGauge?.chart?.totalEnergyUsed}
                      min={selectedData?.energyConsumptionGauge?.chart?.minValue}
                      max={selectedData?.energyConsumptionGauge?.chart?.maxValue}
                      expectedLow={selectedData?.energyConsumptionGauge?.chart?.expectedRangeLow}
                      expectedHigh={selectedData?.energyConsumptionGauge?.chart?.expectedRangeHigh}
                      label="Total energy consumed"
                    />
                    }
                </Box>
              </Stack>
            </Paper>
          </Box>

          {/* Your solar usage */}
          <Box sx={{ marginBottom: custom.spacing.lg }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="szHeading.Bold.Small" 
                id="selfConsumptionTitle" gutterBottom>
                {selectedData?.selfConsumptionChart?.title}
              </Typography>
              <CustomToolTip message={selectedData?.selfConsumptionChart?.tooltip} >
              <SvgIcon sx={{ fontSize: 20 }}>{hollowCircleInfoIcon}</SvgIcon>
              </CustomToolTip>
            </Stack>
            <Paper
              id="solarUsage"
              elevation={0}
              sx={{
                // height: "180px",
                padding: custom.spacing.md,
                border: custom.border,
                borderRadius: custom.corners3,
              }}>
              <Stack direction={{
                      xs: "column-reverse",
                      sm: "column-reverse",
                      md: "column-reverse",
                      lg: data?.rightPanel?"column-reverse":"row"
                    }} 
                    justifyContent="space-between"
                    alignItems={"center"}>
                <Box width={{
                        xs:"100%",
                        sm:"100%",
                        md:"100%",
                        lg:data?.rightPanel?"100%":"450px"
                      }} 
                      mt={custom.spacing.md}>
                  <Stack direction="column" justifyContent="space-between">
                    <Typography variant="szHeading.Bold.xSmall" 
                      id="selfConsumptionMessageTitle" gutterBottom>
                      {selectedData?.selfConsumptionChart?.messageTitle}
                    </Typography>
                    <Typography variant="szBody.Regular.Small"
                      id="selfConsumptionMessage"> 
                      {selectedData?.selfConsumptionChart?.message}
                    </Typography>
                  </Stack>
                </Box>
                <Box maxWidth={{
                        xs:"200px",
                        sm:"250px",
                        md:data?.rightPanel?"250px":"320px",
                        lg:data?.rightPanel?"250px":"320px"
                      }} 
                      display="flex"
                      justifyContent={"center"}>
                  <EnergyConsumptionChart 
                    solarIconPath={solarIconPath} 
                    chart={selectedData?.selfConsumptionChart?.chart}
                    rightPanel={data?.rightPanel} />
                </Box>
              </Stack>
            </Paper>
          </Box>

          {!pdf &&  
            <Box sx={{ marginBottom: custom.spacing.lg }} >
              <Grid container spacing={custom.spacing.md}>
                {/* Got questions? */}
                <Grid item lg={selectedData?.savingsSummary?.actualSavings>0?6:12}>
                  <Paper
                    id="contactUsBox"
                    elevation={0}
                    sx={{ padding: custom.spacing.md, 
                          border: custom.border, 
                          borderRadius: custom.corners5,
                          minHeight: data?.rightPanel?"180px":"140px"
                        }}>
                      <Stack direction="column" justifyContent="space-between">
                          <Typography variant="szHeading.Bold.Small" gutterBottom>
                            Got questions?
                          </Typography>
                          <Typography variant="szBody.Regular.Small">
                            We're here to help. Click the button below, and we'll schedule a call with one of our energy
                            experts at a time that suits.
                          </Typography>
                          <Button variant="contained" 
                                id="contactUs"
                                onClick={handleContactUs}
                                sx={{ 
                                  mt: custom.spacing.md,
                                  borderRadius: custom.corners12px, 
                                  height: custom.sizes.lg,
                                  width: '140px' 
                                }}>
                            <Typography
                              px={custom.spacing.xs}
                              py={custom.spacing.xxs}
                              variant="szBody.Medium.Small"
                              color="#FFF" noWrap>
                            Contact us
                            </Typography>     
                          </Button>
                    </Stack>
                  </Paper>
                </Grid>
                {/* Refer-a-Friend */}
                {selectedData?.savingsSummary?.actualSavings>0 && 
                <Grid item lg={6}>
                  <Paper
                    id="referFriendBox"
                    elevation={0}
                    sx={{ padding: custom.spacing.md, 
                        border: custom.border, 
                        borderRadius: custom.corners5,
                        minHeight: data?.rightPanel?"180px":"140px"
                      }}>
                      <Stack direction="column" justifyContent="space-between" >
                        <Typography variant="szHeading.Bold.Small" gutterBottom>
                          Refer-a-Friend
                        </Typography>
                        <Typography variant="szBody.Regular.Small">
                          Help a friend switch to solar and get rewarded. Share your unique code and earn $1,000 for
                          each person who installs a solar energy system with us 
                        </Typography>
                        <Stack direction={{
                            xs: "column",
                            sm: "row",
                            md: "row",
                            lg: "row", 
                          }} 
                          justifyContent="space-between"
                          mt={custom.spacing.md}>
                          <Button variant="contained" 
                              id="referNow"
                              onClick={handleReferral}
                              sx={{ 
                                borderRadius: custom.corners12px, 
                                height: custom.sizes.lg,
                                width: '140px'  
                              }}>
                            <Typography
                              px={custom.spacing.xs}
                              py={custom.spacing.xxs}
                              variant="szBody.Medium.Small"
                              color="#FFF" noWrap>
                              Refer now
                            </Typography>     
                          </Button>
                        </Stack>
                      </Stack>
                  </Paper>
                </Grid>
                }
              </Grid>
            </Box>
          }
        </Box>
    </Paper>
  </Box>
  )
}
